<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="originalContent"
    :class="{ 'tw:hidden': !disabled }"
    data-testid="original"
  >
    <slot />
  </div>
  <div
    v-if="html"
    v-bind="$attrs"
    :class="{ 'tw:hidden': disabled }"
    data-testid="output"
    v-html="typedContent"
  />
  <AIAssistantBaseChatMarkdown
    v-else
    :class="{ 'tw:hidden': disabled }"
    data-testid="output"
    :source="typedContent"
  />
</template>

<script setup lang="ts">
import { promiseTimeout } from "@vueuse/core";
import { random } from "lodash";
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

import AIAssistantBaseChatMarkdown from "./AIAssistantBaseChatMarkdown.vue";

interface AIAssistantBaseChatTypeProps {
  delay?: number;
  disabled?: boolean;
  html?: boolean;
}

defineOptions({
  inheritAttrs: false,
});

const { delay = 0, disabled } = defineProps<AIAssistantBaseChatTypeProps>();

const emit = defineEmits(["complete"]);

const originalContent = useTemplateRef<HTMLElement>("originalContent");
const typedContent = ref<string>("");

let nodes: string[] = [];
let nodeIndex = 0;

async function type() {
  if (nodeIndex >= nodes.length) {
    emit("complete");

    return;
  }

  typedContent.value +=
    nodes[nodeIndex] + (nodeIndex < nodes.length ? " " : "");

  await promiseTimeout(random(25, 50));
  nodeIndex += 1;
  type();
}

async function update() {
  if (delay) {
    await promiseTimeout(delay);
  }

  const newNodes: string[] = [];

  originalContent.value?.childNodes.forEach((node) => {
    if (node.nodeName === "#comment") {
      return;
    }

    if (node.nodeName === "#text") {
      if (!(node as Text).textContent) {
        return;
      }

      newNodes.push(...(node as Text).wholeText.trim().split(" "));
    } else {
      newNodes.push((node as HTMLElement).outerHTML);
    }
  });

  if (newNodes.toString() === nodes.toString()) {
    return;
  }

  nodes = newNodes;
  nodeIndex = 0;
  typedContent.value = "";

  type();
}

let observer: MutationObserver;

onMounted(() => {
  if (!originalContent.value) return;
  observer = new MutationObserver(update);

  observer.observe(originalContent.value, {
    characterData: true,
    childList: true,
    subtree: true,
  });

  update();
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>
